import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { complaints, type AdminComplaint } from "@/api/complaints";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

export function AdminComplaintsPage() {
  const [status, setStatus] = useState<AdminComplaint["status"] | "">("open");
  const [overdueOnly, setOverdueOnly] = useState<boolean>(false);
  const [target, setTarget] = useState<AdminComplaint | null>(null);

  const list = useQuery({
    queryKey: ["admin-complaints", status, overdueOnly],
    queryFn: () =>
      complaints.admin.list({
        status: status || undefined,
        overdue_only: overdueOnly || undefined,
      }),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Complaints queue</h1>
          <p className="text-2xs text-term-mute">
            Regulatory clock runs from <code>regulatory_clock_start_at</code>.
            CySEC SLA is 90 days; FCA is 56 days. Resolve or reject before
            the SLA window closes.
          </p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
            />
            Overdue only
          </label>
          <Select
            className="w-44"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="open">Open</option>
            <option value="under_review">Under review</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </Select>
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <p className="p-3 text-sm text-term-mute">Loading…</p>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="Empty queue"
              description="Nothing to triage. New complaints land here automatically."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Filed</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">User</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Type</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Subject</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">SLA</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((c) => {
                  const overdue =
                    c.resolved_at == null && new Date(c.sla_due_at) <= new Date();
                  return (
                    <tr key={c.id} className="border-t border-term-border">
                      <td className="px-3 py-1.5 text-term-dim">
                        {new Date(c.opened_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="text-term-fg">{c.user_email ?? "—"}</div>
                        {c.user_name && (
                          <div className="text-2xs text-term-dim">{c.user_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <Badge>{c.classification}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-term-fg">{c.subject}</td>
                      <td className="px-3 py-1.5">{statusBadge(c.status)}</td>
                      <td className={`px-3 py-1.5 ${overdue ? "text-red-500 font-medium" : "text-term-dim"}`}>
                        {new Date(c.sla_due_at).toLocaleDateString()}
                        {overdue && " (overdue)"}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Button onClick={() => setTarget(c)}>Open</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {target && <ResolveModal complaint={target} onClose={() => setTarget(null)} />}
    </div>
  );
}

function statusBadge(s: AdminComplaint["status"]) {
  const tone =
    s === "resolved"
      ? "good"
      : s === "rejected"
      ? "bad"
      : s === "under_review"
      ? "warn"
      : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}

function ResolveModal({
  complaint,
  onClose,
}: {
  complaint: AdminComplaint;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<AdminComplaint["status"]>(complaint.status);
  const [note, setNote] = useState(complaint.resolution_note ?? "");
  const [err, setErr] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: () =>
      complaints.admin.patch(complaint.id, {
        status,
        resolution_note: note || undefined,
      }),
    onSuccess: () => {
      toast.push("Complaint updated", "info");
      qc.invalidateQueries({ queryKey: ["admin-complaints"] });
      onClose();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Complaint · ${complaint.classification}`} size="lg">
      <div className="space-y-3">
        <div>
          <Label>Subject</Label>
          <p className="text-sm font-medium">{complaint.subject}</p>
        </div>
        <div>
          <Label>Detail</Label>
          <pre className="whitespace-pre-wrap rounded bg-term-elev p-2 text-2xs text-term-dim">
            {complaint.body}
          </pre>
        </div>
        <div>
          <Label>Filed by</Label>
          <p className="text-xs text-term-dim">
            {complaint.user_email}
            {complaint.user_name ? ` · ${complaint.user_name}` : ""}
          </p>
        </div>
        <div>
          <Label>Status</Label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as AdminComplaint["status"])}
          >
            <option value="open">open</option>
            <option value="under_review">under_review</option>
            <option value="resolved">resolved</option>
            <option value="rejected">rejected</option>
          </Select>
        </div>
        <div>
          <Label>Resolution note</Label>
          <textarea
            className="block h-24 w-full rounded border border-slate-300 dark:border-slate-700 bg-transparent p-2 text-xs"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => patch.mutate()} loading={patch.isPending}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
